import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { changeVehicleCreators, changeVehicleSelectors } from '../../store';

type RowActionRequests = {
    applyVehicle: () => void;
    isVehicleValid?: boolean;
    isChangeProcessing: boolean;
    isAddNewScenarioDisabled: boolean;
    onAddNewScenario: () => void;
};

/**
 * Watches for a request raised for an action the footer owns: applying the selected vehicle, or creating a
 * scenario from it. A result row raises one, and so does an alternative vehicle options card. Neither can carry it
 * out itself — only the footer knows whether this host confirms first, and whether returning to the dashboard means
 * closing a slideout or navigating.
 *
 * Each request is cleared before it is acted on, so a re-render cannot run it twice, and the footer's own guards decide
 * whether it runs at all rather than trusting the row's button to have been disabled.
 */
export const useRowActionRequests = ({
    applyVehicle,
    isVehicleValid,
    isChangeProcessing,
    isAddNewScenarioDisabled,
    onAddNewScenario
}: RowActionRequests) => {
    const dispatch = useDispatch();
    const isApplyVehicleRequested = useSelector(changeVehicleSelectors.isApplyVehicleRequested);
    const isAddNewScenarioRequested = useSelector(changeVehicleSelectors.isAddNewScenarioRequested);

    useEffect(() => {
        if (!isApplyVehicleRequested) {
            return;
        }

        dispatch(changeVehicleCreators.setApplyVehicleRequested(false));

        if (isVehicleValid && !isChangeProcessing) {
            applyVehicle();
        }
    }, [isApplyVehicleRequested, isVehicleValid, isChangeProcessing, applyVehicle, dispatch]);

    useEffect(() => {
        if (!isAddNewScenarioRequested) {
            return;
        }

        dispatch(changeVehicleCreators.setAddNewScenarioRequested(false));

        if (!isAddNewScenarioDisabled) {
            onAddNewScenario();
        }
    }, [isAddNewScenarioRequested, isAddNewScenarioDisabled, onAddNewScenario, dispatch]);
};
